/**
 * WeblabReviewSummary — the Review view's top summary tiles.
 *
 * Ported from the standalone tracker's DetailsPanelSummary.tsx. Two tiles:
 *   - Launched weblab row count
 *   - Impact totals: summed annualized impact per metric family
 *
 * Formatting matches the tracker: currencyFormatter for all impact rows
 * EXCEPT SP Clicks and Paid Units, which use numberFormatter.
 */

import React, { useMemo } from "react";
import { Grid, List, ListItem, Paper, Typography } from "@mui/material";
import type { WeblabRow } from "./weblabColumns";
import { currencyFormatter, numberFormatter } from "./weblabFormatters";

interface WeblabReviewSummaryProps {
  rows: WeblabRow[];
}

const IMPACT_FIELDS: {
  label: string;
  field: keyof WeblabRow;
  isCount: boolean;
}[] = [
  { label: "SP Revenue", field: "sp_revenue_impact", isCount: false },
  { label: "OPS", field: "ops_impact", isCount: false },
  { label: "GCCP", field: "gccp_impact", isCount: false },
  { label: "Net CP", field: "net_cp_impact", isCount: false },
  { label: "SP Clicks", field: "sp_clicks_impact", isCount: true },
  { label: "Paid Units", field: "paid_units_impact", isCount: true },
  { label: "CSales", field: "csales_impact", isCount: false },
  {
    label: "SP Attr. OPS (1-day)",
    field: "sp_attributed_ops_1day",
    isCount: false,
  },
  { label: "SB Revenue", field: "sb_revenue_impact", isCount: false },
  { label: "SB Clicks", field: "sb_clicks_impact", isCount: true },
];

const WeblabReviewSummary: React.FC<WeblabReviewSummaryProps> = ({ rows }) => {
  const impactTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const f of IMPACT_FIELDS) totals[f.field as string] = 0;

    for (const row of rows) {
      for (const f of IMPACT_FIELDS) {
        const n = Number(row[f.field as string]);
        if (!Number.isNaN(n)) totals[f.field as string] += n;
      }
    }
    return totals;
  }, [rows]);

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} md={6}>
        <Paper elevation={3} sx={{ p: 1 }}>
          <List dense>
            <ListItem divider>
              <Grid container>
                <Grid item xs={7}>
                  <Typography variant="body2" fontWeight={700}>
                    Launched Weblabs
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="body2" fontWeight={700}>
                    Count
                  </Typography>
                </Grid>
              </Grid>
            </ListItem>
            <ListItem divider>
              <Grid container>
                <Grid item xs={7}>
                  <Typography variant="body2">Total</Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="body2">{rows.length}</Typography>
                </Grid>
              </Grid>
            </ListItem>
          </List>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper elevation={3} sx={{ p: 1 }}>
          <List dense>
            <ListItem divider>
              <Grid container>
                <Grid item xs={7}>
                  <Typography variant="body2" fontWeight={700}>
                    Impact
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="body2" fontWeight={700}></Typography>
                </Grid>
              </Grid>
            </ListItem>
            {IMPACT_FIELDS.map((f) => {
              const total = impactTotals[f.field as string];
              return (
                <ListItem key={f.field as string} divider>
                  <Grid container>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={600}>
                        {f.label}
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="body2">
                        {f.isCount
                          ? numberFormatter(total)
                          : currencyFormatter(total)}
                      </Typography>
                    </Grid>
                  </Grid>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default WeblabReviewSummary;
